import { ApiError } from "@/generated-api";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/authStore";
import { useLoginWithApple } from "@/tanstack-hooks";
import { TRANSLATIONS } from "@/translations";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { Button } from "../ui/button";
import SpinnerSmall from "../SpinnerSmall";
import appleIcon from "../../assets/apple.svg";
import { SUCCESSFUL_LOGIN_TYPE, SuccessfulLoginType } from "./SignInFlowTypes";
import { clearStoredUtms, getStoredUtms } from "@/lib/utms";

declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: {
            id_token: string;
          };
        }>;
      };
    };
  }
}

export default function LoginWithApple({
  fingerprint,
}: {
  fingerprint: string;
}) {
  const { toast } = useToast();
  const { setToken } = useAuthStore();
  const navigate = useNavigate();
  const loginWithAppleMutation = useLoginWithApple();
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const isLoading = isAuthorizing || loginWithAppleMutation.isPending;
  const redirectURI =
    import.meta.env.VITE_DEPLOYMENT_ENV === "staging"
      ? "https://staging.pulse-date.com/apple"
      : "https://app.pulse-date.com/apple";

  useEffect(() => {
    if (isNative) return;
    if (window.AppleID) {
      window.AppleID.auth.init({
        clientId: "com.pulse-date.serviceid",
        scope: "email",
        redirectURI: redirectURI,
        usePopup: true,
      });
    }
  }, [isNative, redirectURI]);

  async function getIdTokenNative(): Promise<string | null> {
    const result = await SignInWithApple.authorize({
      clientId: "com.pulsedate.app",
      redirectURI: redirectURI,
      scopes: "email",
      state: crypto.randomUUID(),
    });
    return result.response?.identityToken ?? null;
  }

  async function getIdTokenWeb(): Promise<string | null> {
    if (!window.AppleID) {
      toast({
        variant: "destructive",
        title: "Apple Sign-In not available",
        description: "Please try again later",
        className: "dark",
      });
      return null;
    }
    const response = await window.AppleID.auth.signIn();
    return response.authorization.id_token ?? null;
  }

  async function handleLoginWithApple() {
    setIsAuthorizing(true);
    // The native Apple bridge blocks the WebView while the modal opens, so the
    // spinner never paints if we call it on the same tick. Wait for two animation
    // frames to let React commit and the browser paint the spinner first.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    try {
      const idToken = isNative
        ? await getIdTokenNative()
        : await getIdTokenWeb();

      if (!idToken) {
        toast({
          variant: "destructive",
          title: "Error logging in with Apple",
          description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
        return;
      }

      loginWithAppleMutation.mutate(
        { id_token: idToken, fingerprint, ...getStoredUtms() },
        {
          onSuccess: (data) => {
            setToken(data.access_token);
            localStorage.setItem(
              SUCCESSFUL_LOGIN_TYPE,
              SuccessfulLoginType.APPLE,
            );
            clearStoredUtms();
            navigate("/");
          },
          onError: (error) => {
            const err = error as ApiError;
            if (err.status === 423) {
              toast({
                variant: "destructive",
                title: "Error logging in with Apple",
                description:
                  "Account not found. Need to reset Apple Sign-In. Settings > [Your Name] > Sign in with Apple > Pulse > Delete",
                className: "dark",
              });
            } else {
              toast({
                variant: "destructive",
                title: "Error logging in with Apple",
                description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
                className: "dark",
              });
            }
          },
        },
      );
    } catch (error) {
      if (
        (error &&
          typeof error === "object" &&
          "error" in error &&
          (error.error === "popup_closed_by_user" ||
            error.error === "user_cancelled_authorize")) ||
        (error &&
          typeof error === "object" &&
          "message" in error &&
          typeof error.message === "string" &&
          (error.message.includes("popup_closed_by_user") ||
            error.message.includes("user_cancelled") ||
            error.message.includes("canceled") ||
            error.message.includes("error 1001")))
      ) {
        console.log("User dismissed Apple Sign-In");
        return;
      }

      console.error("Apple Sign In error:", error);
      toast({
        variant: "destructive",
        title: "Error logging in with Apple",
        description: TRANSLATIONS.ENGLISH.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
    } finally {
      setIsAuthorizing(false);
    }
  }

  return (
    <Button
      onClick={() => void handleLoginWithApple()}
      disabled={isLoading}
    >
      {isLoading ? (
        <SpinnerSmall black={true} />
      ) : (
        <img src={appleIcon} alt="Apple Logo" className="w-5" />
      )}
    </Button>
  );
}
